import {
  BACKEND_UNAVAILABLE_ID,
  isBackendUnavailableError,
  useBackendHealthStore,
} from "@/stores/useBackendHealthStore";

// Canonical hubCall<T>. All `*.service.ts` files MUST import from here
// instead of declaring their own copy (ESLint enforces).
//
// Same shape as the original per-service inline copies in admin-nextjs:
//   - posts to `/api/hub${path}` (worker proxy or Vite dev forward).
//   - sends cookies (`credentials: "include"`) so the worker can read the
//     HttpOnly access/refresh tokens + the org cookie.
//   - JSON content-type + caller-overridable headers.
//   - on non-2xx, throws an ApiError with backend's reported message
//     (falling back to a generic string when the body is empty/non-JSON).
// Public — also imported by workflow.service for SSE stream URL construction.
export const HUB_API = "/api/hub";

export interface ApiError {
  message: string;
  statusCode?: number;
  /** HTTP status echoed by BE (data.error.code). String for transition tolerance: pre-refactor was a slug, post-refactor is the numeric HTTP code. */
  code?: string;
  /** Slug ID emitted by BE (data.error.id). Stable identifier for field-level error routing. */
  id?: string;
}

export async function hubCall<T>(
  path: string,
  options: RequestInit = {},
  fallbackMessage = "Request failed",
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${HUB_API}${path}`, {
      ...options,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...options.headers },
    });
  } catch {
    const error: ApiError = {
      message: "Cannot connect to backend",
      statusCode: 0,
      id: BACKEND_UNAVAILABLE_ID,
    };
    useBackendHealthStore.getState().reportBackendFailure(error.message);
    throw error;
  }

  // Read the body once. Tolerate empty 204 / Content-Length:0 responses;
  // otherwise a malformed JSON body must surface as a real error instead of
  // silently becoming `null` — that swallow previously let callers (notably
  // the KG replay flow) fire a fake "queued" toast on a body the FE could
  // not parse.
  const isEmptyBody = response.status === 204 || response.headers.get("content-length") === "0";

  let data: unknown = null;
  let parseFailed = false;
  if (!isEmptyBody) {
    try {
      data = await response.json();
    } catch {
      parseFailed = true;
    }
  }

  if (!response.ok) {
    // BE returns 4xx in two wire shapes (see CLAUDE.md "Error handling pattern"):
    //   - Wrapped `{ error: { id, code, status, message } }` from the Recovery
    //     middleware (panic on typed sentinel in controllers).
    //   - Flat `{ id, code, status, message }` from `c.AbortWithStatusJSON(...)`
    //     called directly — auth middleware uses this for gate rejections
    //     (account_pending_approval, account_suspended, …).
    // Read both shapes so `err.id` is populated regardless of which path the
    // error took.
    // Flat shape's `code` field is the numeric HTTP status (already captured by
    // `statusCode`), so we only read wrapped envelope `error.code` for the
    // BE-defined string code. The `id` slug is the value that drives FE pivots.
    const errorEnvelope = data as {
      error?: { message?: string; code?: string; id?: string };
      id?: string;
      message?: string;
    } | null;
    const error: ApiError = {
      message: errorEnvelope?.error?.message || errorEnvelope?.message || fallbackMessage,
      statusCode: response.status,
      code: errorEnvelope?.error?.code,
      id: errorEnvelope?.error?.id || errorEnvelope?.id,
    };
    if (isBackendUnavailableError(error)) {
      error.id = BACKEND_UNAVAILABLE_ID;
      useBackendHealthStore.getState().reportBackendFailure(error.message);
    }
    throw error;
  }

  if (parseFailed) {
    const error: ApiError = {
      message: "Server returned a malformed response body",
      statusCode: 502,
      id: "invalid_response_body",
    };
    throw error;
  }

  useBackendHealthStore.getState().reportBackendSuccess();
  return data as T;
}

export function publicHubCall<T>(
  path: string,
  options: RequestInit = {},
  fallbackMessage = "Request failed",
): Promise<T> {
  return hubCall<T>(path, options, fallbackMessage);
}
